import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Storefront } from "@phosphor-icons/react";
import { acceptPendingInvite, persistInviteToken } from "@/lib/invites";
import { useGoogleSignInEnabled } from "@/lib/google_auth_enabled";

const HERO = "https://images.unsplash.com/photo-1551529563-fce9529e67ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxjYWZlJTIwY291bnRlciUyMHBvaW50JTIwb2YlMjBzYWxlfGVufDB8fHx8MTc3OTE0MTE3MHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const googleEnabled = useGoogleSignInEnabled();
  // iter_219 — Marketing pricing endpoint removed alongside the
  // legacy billing module. The Subscribe flow lives in Settings >
  // Subscription and reads pricing from the versioned
  // `subscription_plans` collection at runtime.

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), pw);
      // Accept any pending invitation (URL or sessionStorage)
      try {
        const accepted = await acceptPendingInvite();
        if (accepted) { await refresh(); toast.success("Invitation accepted"); }
      } catch (e) { toast.error(e?.detail || "Invite could not be accepted"); }
      toast.success("Welcome back");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--sand-50)]">
      <div className="hidden md:block relative">
        <img src={HERO} alt="cafe" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(28,25,23,0.6)] via-[rgba(28,25,23,0.35)] to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-md bg-[var(--terracotta)] flex items-center justify-center">
              <Storefront size={20} color="#fff" weight="fill" />
            </div>
            <div className="font-heading font-semibold text-lg">Margin Mate One POS</div>
          </div>
          <h2 className="font-heading text-3xl font-semibold leading-tight max-w-md">
            One system. One order engine. One ticket.
          </h2>
          <p className="text-sm mt-3 max-w-md opacity-90" data-testid="login-price-label">
            Run POS, KDS, ordering, stock, recipes, delivery and loyalty from a single connected cloud platform.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="mb-8">
            <div className="label-eyebrow mb-2">Sign in</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">Welcome back</h1>
            <p className="text-sm text-[var(--ink-500)] mt-2">Enter your credentials to access your venue.</p>
          </div>
          {inviteToken && (
            <div className="mb-5 rounded-md border border-[var(--moss-bg)] bg-[var(--moss-bg)]/40 px-3 py-2.5 text-xs text-[var(--ink-700)]" data-testid="login-invite-banner">
              You're accepting a team invitation. Sign in to continue.
            </div>
          )}
          <label className="block text-xs font-medium mb-1.5">Email</label>
          <input
            type="email"
            required
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white mb-4"
            placeholder="you@cafe.com"
          />
          <label className="block text-xs font-medium mb-1.5">Password</label>
          <input
            type="password"
            required
            data-testid="login-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] focus:border-[var(--terracotta)] focus:ring-2 focus:ring-[var(--terracotta)]/20 outline-none bg-white mb-6"
            placeholder="••••••••"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-11 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] active:bg-[var(--terracotta-darker)] active:scale-[0.99] text-white font-medium transition-all duration-200 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          {googleEnabled && (
          <>
          <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-[0.18em] text-[var(--ink-500)]">
            <span className="flex-1 h-px bg-[rgba(28,25,23,0.08)]"></span> or <span className="flex-1 h-px bg-[rgba(28,25,23,0.08)]"></span>
          </div>
          <button
            type="button"
            data-testid="login-google"
            onClick={() => {
              if (inviteToken) persistInviteToken(inviteToken);
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/google/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 rounded-md border border-[rgba(28,25,23,0.12)] bg-white hover:bg-[var(--sand-100)] text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC04" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>
          </>
          )}
          <p className="text-sm text-[var(--ink-500)] mt-6 text-center">
            New here?{" "}
            <Link to="/signup" data-testid="goto-signup" className="text-[var(--terracotta)] hover:underline font-medium">Start free trial</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
